import React, { useState, useEffect, useMemo } from 'react';
import { Button, SchemaIcon, LucideIconPicker, SearchableDropdown } from './common';
import { Plus, Trash2, GripVertical, ChevronDown, ChevronUp, GitFork, Lock } from 'lucide-react';
import { schemaLabel } from '../utils/schemaUtils';
import './SchemaBuilder.css';

/**
 * Human-friendly field type options.
 * Maps user-facing labels to JSON Schema types + formats.
 */
const FIELD_TYPES = [
  { label: 'Text',           value: 'string',         icon: 'Aa',  jsonType: 'string' },
  { label: 'Long Text',      value: 'string-long',    icon: '¶',   jsonType: 'string', meta: { maxLength: 5000 } },
  { label: 'Number',         value: 'number',         icon: '#',   jsonType: 'number' },
  { label: 'Whole Number',   value: 'integer',        icon: '1',   jsonType: 'integer' },
  { label: 'Yes / No',       value: 'boolean',        icon: '◉',  jsonType: 'boolean' },
  { label: 'Date',           value: 'date',           icon: '📅', jsonType: 'string', meta: { format: 'date' } },
  { label: 'Date & Time',    value: 'datetime',       icon: '🕐', jsonType: 'string', meta: { format: 'date-time' } },
  { label: 'Email',          value: 'email',          icon: '@',   jsonType: 'string', meta: { format: 'email' } },
  { label: 'URL',            value: 'url',            icon: '🔗', jsonType: 'string', meta: { format: 'uri' } },
  { label: 'List of Items',  value: 'array',          icon: '[]',  jsonType: 'array', meta: { items: { type: 'string' } } },
  { label: 'Choice (one of…)', value: 'enum',         icon: '▾',  jsonType: 'string', meta: {} },
];

function fieldTypeFromProp(prop) {
  if (prop.enum) return 'enum';
  if (prop.type === 'array') return 'array';
  if (prop.type === 'boolean') return 'boolean';
  if (prop.type === 'integer') return 'integer';
  if (prop.type === 'number') return 'number';
  if (prop.type === 'string') {
    if (prop.format === 'date') return 'date';
    if (prop.format === 'date-time') return 'datetime';
    if (prop.format === 'email') return 'email';
    if (prop.format === 'uri') return 'url';
    if (prop.maxLength && prop.maxLength > 200) return 'string-long';
    return 'string';
  }
  return 'string';
}

function propFromFieldType(type, extra = {}) {
  const def = FIELD_TYPES.find(t => t.value === type) || FIELD_TYPES[0];
  const prop = { type: def.jsonType, ...(def.meta || {}) };
  if (extra.description) prop.description = extra.description;
  if (type === 'enum' && extra.enumValues?.length) {
    prop.enum = extra.enumValues;
  }
  return prop;
}

function formatFieldName(key) {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

function slugifyFieldName(name) {
  const trimmed = name.trim();
  // If already a valid identifier (no spaces/special chars), preserve as-is
  if (/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(trimmed)) return trimmed;
  // Otherwise convert spaces to underscores and strip invalid chars
  return trimmed
    .replace(/\s+/g, '_')
    .replace(/[^a-zA-Z0-9_]/g, '');
}

// Convert JSON Schema properties to our field list format
function propsToFields(properties, required = []) {
  if (!properties || typeof properties !== 'object') return [];
  return Object.entries(properties).map(([key, prop]) => ({
    key,
    label: formatFieldName(key),
    type: fieldTypeFromProp(prop),
    description: prop.description || '',
    required: required.includes(key),
    encrypted: false,
    enumValues: prop.enum || [],
    expanded: false,
  }));
}

// Convert our field list back to JSON Schema
function fieldsToJsonSchema(fields) {
  const properties = {};
  const required = [];
  for (const field of fields) {
    if (!field.key) continue;
    properties[field.key] = propFromFieldType(field.type, {
      description: field.description,
      enumValues: field.enumValues,
    });
    if (field.required) required.push(field.key);
  }
  return { type: 'object', properties, required };
}

const EMPTY_FIELD = {
  key: '',
  label: '',
  type: 'string',
  description: '',
  required: false,
  encrypted: false,
  enumValues: [],
  expanded: true,
};


export default function SchemaBuilder({
  // mode: 'create' | 'extend' | 'update'
  mode = 'create',
  // For extend: the parent schema data (full object with json_schema)
  parentSchema = null,
  // For update: the existing schema being versioned
  existingSchema = null,
  // All schemas for the extends dropdown
  allSchemas = [],
  // Called when user saves
  onSave,
  // Called when user cancels
  onCancel,
  // Called when user deletes (update mode, private schemas only)
  onDelete = null,
}) {
  // Schema metadata
  const [schemaName, setSchemaUri] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [scope, setScope] = useState('public');
  const [extendsId, setExtendsId] = useState('');
  const [icon, setIcon] = useState(null);

  // Fields
  const [fields, setFields] = useState([{ ...EMPTY_FIELD }]);
  const [saving, setSaving] = useState(false);

  // Parent schema fields (for extend mode display)
  const [parentFields, setParentFields] = useState([]);

  // Initialize from existing schema (update mode)
  useEffect(() => {
    if (mode === 'update' && existingSchema) {
      setSchemaUri(existingSchema.schema_name || '');
      setDescription(existingSchema.description || '');
      setCategory(existingSchema.category || '');
      setScope(existingSchema.scope || 'public');
      setExtendsId(existingSchema.extends || '');
      setIcon(existingSchema.schema_metadata?.icon || null);

      const existingFields = propsToFields(
        existingSchema.json_schema?.properties,
        existingSchema.json_schema?.required || []
      ).map(f => ({ ...f, expanded: false, encrypted: (existingSchema.encrypted_fields || []).includes(f.key) }));
      setFields(existingFields.length > 0 ? existingFields : [{ ...EMPTY_FIELD }]);
    }
  }, [mode, existingSchema]);

  // Initialize for extend mode (entered via "Extend this Schema" button)
  useEffect(() => {
    if (mode === 'extend' && parentSchema) {
      setExtendsId(parentSchema.id);
      setCategory(parentSchema.category || '');
      const pFields = propsToFields(
        parentSchema.json_schema?.properties,
        parentSchema.json_schema?.required || []
      );
      setParentFields(pFields);
    }
  }, [mode, parentSchema]);

  // Track the resolved parent name for display
  const [parentName, setParentName] = useState(schemaLabel(parentSchema?.schema_name) || '');

  // When extendsId changes (dropdown selection or update mode init), load parent fields
  useEffect(() => {
    if (!extendsId) {
      // Only clear if we're not in extend mode (where parentSchema prop controls it)
      if (mode !== 'extend') {
        setParentFields([]);
        setParentName('');
      }
      return;
    }
    // Check if already loaded from prop
    if (mode === 'extend' && parentSchema?.id === extendsId) return;

    // Try to find in allSchemas first
    const found = allSchemas.find(s => s.id === extendsId);
    if (found?.json_schema) {
      const pFields = propsToFields(
        found.json_schema.properties,
        found.json_schema.required || []
      );
      setParentFields(pFields);
      setParentName(schemaLabel(found.schema_name) || '');
    }
  }, [extendsId, allSchemas, mode, parentSchema]);

  // Live JSON Schema preview
  const jsonSchema = useMemo(() => fieldsToJsonSchema(fields), [fields]);
  const jsonPreview = useMemo(() => JSON.stringify(jsonSchema, null, 2), [jsonSchema]);

  // Drag-to-reorder state
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const handleDragStart = (index) => {
    setDragIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === index) return;
    setDragOverIndex(index);
  };

  const handleDrop = (index) => {
    if (dragIndex === null || dragIndex === index) {
      setDragIndex(null);
      setDragOverIndex(null);
      return;
    }
    setFields(prev => {
      const arr = [...prev];
      const [moved] = arr.splice(dragIndex, 1);
      arr.splice(index, 0, moved);
      return arr;
    });
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  // Backwards compatibility check (update mode only)
  const compatWarnings = useMemo(() => {
    if (mode !== 'update' || !existingSchema?.json_schema) return [];
    const oldProps = existingSchema.json_schema.properties || {};
    const oldRequired = existingSchema.json_schema.required || [];
    const newFieldKeys = fields.filter(f => f.key).map(f => f.key);
    const warnings = [];

    // Check for removed fields
    for (const key of Object.keys(oldProps)) {
      if (!newFieldKeys.includes(key)) {
        warnings.push(`Removed field "${formatFieldName(key)}" — existing data using this field will lose its schema mapping`);
      }
    }

    // Check for type changes
    for (const field of fields) {
      if (!field.key || !oldProps[field.key]) continue;
      const oldType = fieldTypeFromProp(oldProps[field.key]);
      if (oldType !== field.type) {
        warnings.push(`Changed "${formatFieldName(field.key)}" from ${FIELD_TYPES.find(t => t.value === oldType)?.label || oldType} to ${FIELD_TYPES.find(t => t.value === field.type)?.label || field.type} — existing data may not match`);
      }
    }

    // Check for newly required fields
    for (const field of fields) {
      if (!field.key || !field.required) continue;
      if (oldProps[field.key] && !oldRequired.includes(field.key)) {
        warnings.push(`Made "${formatFieldName(field.key)}" required — existing entries without this field will fail validation`);
      }
    }

    return warnings;
  }, [mode, existingSchema, fields]);

  // Field operations
  const updateField = (index, updates) => {
    setFields(prev => prev.map((f, i) => i === index ? { ...f, ...updates } : f));
  };

  const removeField = (index) => {
    setFields(prev => prev.filter((_, i) => i !== index));
  };

  const addField = () => {
    setFields(prev => [...prev.map(f => ({ ...f, expanded: false })), { ...EMPTY_FIELD }]);
  };

  const toggleExpand = (index) => {
    setFields(prev => prev.map((f, i) => i === index ? { ...f, expanded: !f.expanded } : f));
  };

  // Handle name change for a field — auto-generate key from label
  const handleFieldLabelChange = (index, label) => {
    const key = slugifyFieldName(label);
    updateField(index, { label, key });
  };

  // Submit
  const handleSubmit = async () => {
    if (!schemaName.trim() || saving) return;
    const validFields = fields.filter(f => f.key);
    if (validFields.length === 0) return;

    const encFields = validFields.filter(f => f.encrypted).map(f => f.key);
    const schema = fieldsToJsonSchema(validFields);

    setSaving(true);
    try {
      await onSave({
        schema_name: schemaName.trim(),
        description: description.trim() || undefined,
        category: category.trim() || undefined,
        scope,
        extends: extendsId || undefined,
        schema_metadata: icon ? { icon } : undefined,
        encrypted_fields: encFields,
        json_schema: schema,
      });
    } finally {
      setSaving(false);
    }
  };

  const title = mode === 'update'
    ? `Update ${schemaLabel(existingSchema?.schema_name) || 'Schema'}`
    : mode === 'extend'
      ? `Extend ${parentName || 'Schema'}`
      : 'Create a New Schema';

  const subtitle = mode === 'update'
    ? `This will create version ${(existingSchema?.version || 0) + 1}. The current version is preserved.`
    : mode === 'extend'
      ? `Your schema inherits all fields from ${parentName || 'the parent'}. Add your own fields below.`
      : 'Define what information this schema captures. Think of each field as a question you want answered.';

  return (
    <div className="sbuilder">
      {/* Header */}
      {/* This is a form with unsaved work, so leaving it is Cancel, not "back" —
          it discards. It also returns to the previous view when the builder is
          opened from Extend or Update, which no sidebar item can do. It sits
          with the primary action because that is the choice being made. */}
      <div className="sbuilder__header">
        <div className="sbuilder__header-text">
          <h1 className="sbuilder__title">{title}</h1>
          <p className="sbuilder__subtitle">{subtitle}</p>
        </div>
        <div className="sbuilder__header-actions">
          <Button variant="secondary" onClick={onCancel}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={saving || !schemaName.trim() || fields.filter(f => f.key).length === 0} loading={saving}>
            {mode === 'update' ? `Save as v${(existingSchema?.version || 0) + 1}` : 'Create Schema'}
          </Button>
        </div>
      </div>

      <div className="sbuilder__body">
        {/* Left: Builder */}
        <div className="sbuilder__editor">
          {/* Schema info */}
          <div className="sbuilder__section">
            <div className="sbuilder__info-grid">
              <div className="sbuilder__field-group sbuilder__field-group--wide">
                <label className="sbuilder__label">Schema URI *</label>
                <input
                  type="text"
                  className="sbuilder__input"
                  placeholder="e.g. custom:medical-record, myapp:recipe"
                  value={schemaName}
                  onChange={e => setSchemaUri(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="sbuilder__field-group sbuilder__field-group--wide">
                <label className="sbuilder__label">Describe it in a sentence</label>
                <input
                  type="text"
                  className="sbuilder__input"
                  placeholder="e.g. A record of a doctor's visit with diagnosis and treatment"
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                />
              </div>
              <div className="sbuilder__field-group">
                <label className="sbuilder__label">Category</label>
                <input
                  type="text"
                  className="sbuilder__input"
                  placeholder="e.g. Health, Finance"
                  value={category}
                  onChange={e => setCategory(e.target.value)}
                />
              </div>
              <div className="sbuilder__field-group">
                <LucideIconPicker icon={icon} onChangeIcon={setIcon} />
              </div>
              <div className="sbuilder__field-group">
                <label className="sbuilder__label">Visibility</label>
                <select className="sbuilder__input" value={scope} onChange={e => setScope(e.target.value)}>
                  <option value="public">Public — anyone can use it</option>
                  <option value="private">Private — only you and your apps</option>
                </select>
              </div>
              {mode === 'create' && (
                <div className="sbuilder__field-group sbuilder__field-group--wide">
                  <label className="sbuilder__label">Extend an existing schema?</label>
                  <SearchableDropdown
                    items={allSchemas.filter(s => s.scope === 'public' || s.is_system)}
                    getId={s => s.id}
                    getLabel={s => `${schemaLabel(s.schema_name)} (v${s.version})`}
                    getGroup={s => s.category || 'Other'}
                    placeholder="None — start from scratch"
                    value={extendsId || null}
                    onChange={id => setExtendsId(id || '')}
                    allowClear
                    renderIcon={s => <SchemaIcon iconName={s.schema_metadata?.icon} size={14} />}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Parent fields (extend mode) */}
          {parentFields.length > 0 && (
            <div className="sbuilder__section">
              <div className="sbuilder__inherited-header">
                <GitFork size={14} />
                <span>Already inherited from <strong>{parentName}</strong> — no need to redefine these</span>
              </div>
              <div className="sbuilder__inherited-fields">
                {parentFields.map(f => (
                  <div key={f.key} className="sbuilder__inherited-field">
                    <span className="sbuilder__inherited-name">{formatFieldName(f.key)}</span>
                    <span className="sbuilder__inherited-type">
                      {FIELD_TYPES.find(t => t.value === f.type)?.label || f.type}
                    </span>
                    {f.required && <span className="sbuilder__inherited-req">Required</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Fields */}
          <div className="sbuilder__section">
            <h3 className="sbuilder__section-title">
              {parentFields.length > 0 ? 'Additional Fields' : 'Fields'}
            </h3>
            <p className="sbuilder__section-hint">
              What pieces of information should this schema capture?
            </p>

            {compatWarnings.length > 0 && (
              <div className="sbuilder__compat-warning">
                <div className="sbuilder__compat-title">Not backwards compatible</div>
                <ul className="sbuilder__compat-list">
                  {compatWarnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            )}

            <div className="sbuilder__fields-list">
              {fields.map((field, index) => (
                <div
                  key={index}
                  className={`sbuilder__field-card ${field.expanded ? 'sbuilder__field-card--expanded' : ''} ${dragIndex === index ? 'sbuilder__field-card--dragging' : ''} ${dragOverIndex === index ? 'sbuilder__field-card--drag-over' : ''}`}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={e => handleDragOver(e, index)}
                  onDrop={() => handleDrop(index)}
                  onDragEnd={handleDragEnd}
                >
                  <div className="sbuilder__field-row">
                    <div className="sbuilder__field-grip" title="Drag to reorder">
                      <GripVertical size={14} />
                    </div>
                    <div className="sbuilder__field-main">
                      <input
                        type="text"
                        className="sbuilder__field-name-input"
                        placeholder="Field name (e.g. Diagnosis, Amount, Start Date)"
                        value={field.label}
                        onChange={e => handleFieldLabelChange(index, e.target.value)}
                      />
                      <select
                        className="sbuilder__field-type-select"
                        value={field.type}
                        onChange={e => updateField(index, { type: e.target.value })}
                      >
                        {FIELD_TYPES.map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="sbuilder__field-actions">
                      <button
                        className="sbuilder__field-action"
                        onClick={() => toggleExpand(index)}
                        title="More options"
                      >
                        {field.expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </button>
                      {fields.length > 1 && (
                        <button
                          className="sbuilder__field-action sbuilder__field-action--danger"
                          onClick={() => removeField(index)}
                          title="Remove field"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>

                  {field.expanded && (
                    <div className="sbuilder__field-details">
                      <div className="sbuilder__field-detail-row">
                        <label className="sbuilder__detail-label">Help text</label>
                        <input
                          type="text"
                          className="sbuilder__input sbuilder__input--sm"
                          placeholder="Optional hint shown to users filling this in"
                          value={field.description}
                          onChange={e => updateField(index, { description: e.target.value })}
                        />
                      </div>
                      {field.type === 'enum' && (
                        <div className="sbuilder__field-detail-row">
                          <label className="sbuilder__detail-label">Choices</label>
                          <input
                            type="text"
                            className="sbuilder__input sbuilder__input--sm"
                            placeholder="Option A, Option B, Option C"
                            value={(field.enumValues || []).join(', ')}
                            onChange={e => updateField(index, {
                              enumValues: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                            })}
                          />
                          <span className="sbuilder__detail-hint">Separate options with commas</span>
                        </div>
                      )}
                      <div className="sbuilder__field-toggles">
                        <label className="sbuilder__toggle-label">
                          <input
                            type="checkbox"
                            checked={field.required}
                            onChange={e => updateField(index, { required: e.target.checked })}
                          />
                          <span>Required</span>
                        </label>
                        <label className="sbuilder__toggle-label">
                          <input
                            type="checkbox"
                            checked={field.encrypted}
                            onChange={e => updateField(index, { encrypted: e.target.checked })}
                          />
                          <Lock size={12} />
                          <span>Encrypted at rest</span>
                        </label>
                      </div>
                      <div className="sbuilder__field-key-preview">
                        Field key: <code>{field.key || '…'}</code>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <button className="sbuilder__add-field" onClick={addField}>
              <Plus size={16} />
              <span>Add another field</span>
            </button>
          </div>

          {onDelete && (
            <div className="sbuilder__danger-zone">
              <h4 className="sbuilder__danger-title">Danger Zone</h4>
              <div className="sbuilder__danger-content">
                <div className="sbuilder__danger-text">
                  <strong>Delete this schema</strong>
                  <span>This action cannot be undone. Existing nodes using this schema will lose their schema mapping.</span>
                </div>
                <button
                  className="sbuilder__danger-btn"
                  onClick={() => {
                    if (window.confirm(`Delete "${schemaName || 'this schema'}"? This cannot be undone.`)) {
                      onDelete();
                    }
                  }}
                >
                  Delete Schema
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Right: JSON Preview */}
        <div className="sbuilder__preview">
          <div className="sbuilder__preview-header">
            <h4 className="sbuilder__preview-title">JSON Schema Preview</h4>
          </div>
          <pre className="sbuilder__preview-code">{jsonPreview}</pre>
        </div>
      </div>
    </div>
  );
}
